import { Component, Input, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatCellDef,
	MatCell,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import {
	SelectedUserEvent,
	UserSearchBarComponent,
	UserType,
} from 'src/app/components/user-search-bar/user-search-bar.component';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { EditRole } from 'src/app/ui/roles/add-role/add-role-main-tab.component';
import { NotAuthorizedNoticeComponent } from '../../../../components/not-authorized-notice/not-authorized-notice.component';
import { HasSystemCapabilityDirective } from '../../../../directives/system-capabilities/has-system-capability.directive';
import { UserSearchBarComponent as UserSearchBarComponent_1 } from '../../../../components/user-search-bar/user-search-bar.component';

@Component({
	selector: 'app-add-role-users',
	templateUrl: './add-role-users.component.html',
	styleUrls: [
		'./add-role-users.component.scss',
		'../../../common/main.scss',
		'../../../common/stepper.scss',
	],
	imports: [
		NotAuthorizedNoticeComponent,
		HasSystemCapabilityDirective,
		UserSearchBarComponent_1,
		MatButton,
		MatTable,
		MatColumnDef,
		MatCellDef,
		MatCell,
		MatRowDef,
		MatRow,
	],
})
export class AddRoleUsersComponent {
	@ViewChild('addUserButton') addUserButton!: MatButton;
	@ViewChild('userSearchBarComponent')
	userSearchBarComponent!: UserSearchBarComponent;

	@Input() requireUsers = false;
	@Input() companyId?: string;
	@Input() editRole!: EditRole;

	apiLoadingKeys = ApiLoadingKeys;

	newUserEvent?: SelectedUserEvent;
	selectedUserEvents: SelectedUserEvent[] = [];

	displayedColumns: string[] = ['name', 'email', 'phoneNumber', 'remove'];
	dataSource = new MatTableDataSource<SelectedUserEvent>();

	constructor(
		private readonly dialog: MatDialog,
		private readonly stepper: MatStepper,
		private readonly apiService: ApiService,
		private readonly bannerService: BannerMessageService,
		private readonly userProfileService: UserProfileService
	) {}

	onUserSelectedEvent(event: SelectedUserEvent): void {
		switch (event.userType) {
			case UserType.newUser:
				this.newUserEvent = event;
				if (this.addUserButton) {
					this.addUserButton.disabled = event.user.email === '';
				}
				break;
			case UserType.existingUser:
				this.addSelectedUserEvent(event);

				this.userSearchBarComponent?.clear();
				break;
		}
	}

	onAddUserClicked(): void {
		this.addSelectedUserEvent(this.newUserEvent);

		this.userSearchBarComponent?.clear();
		this.newUserEvent = undefined;
	}

	private addSelectedUserEvent(event?: SelectedUserEvent): void {
		if (event !== undefined) {
			const emails = new Set(
				this.selectedUserEvents.map((it) => it.user.email)
			);
			if (!emails.has(event.user.email)) {
				this.selectedUserEvents.push(event);
				this.dataSource.data = this.selectedUserEvents;
				this.editRole.users = this.dataSource.data;
			}
		}
	}

	get isValid(): boolean {
		if (this.addUserButton) {
			if (!this.addUserButton.disabled) {
				return false;
			}
		}

		return !this.requireUsers || this.dataSource.data.length > 0;
	}

	removeUser(selectedUser: SelectedUserEvent): void {
		const dataSourceIndex = this.dataSource.data.indexOf(selectedUser);
		this.dataSource.data.splice(dataSourceIndex, 1);
		this.dataSource._updateChangeSubscription();
		this.editRole.users = this.dataSource.data;
	}

	async checkForExistingRolesThenContinue(
		continueMethod: () => void,
		companyId?: string
	): Promise<void> {
		if (!companyId) {
			companyId = (await this.userProfileService.getUserProfile())
				.activeRoleDetails?.company.id;
		}

		if (!companyId) {
			console.error('No company selected!');
			this.bannerService.sendMessage(
				'Error adding User or Role!',
				BannerMessageType.error
			);
			return;
		}

		const userIds = this.editRole.users
			.filter((value) => value.userType === UserType.existingUser)
			.map((value) => value.user.id);

		let usersExist = false;
		let userProfiles = [];
		if (userIds.length > 0) {
			const queryParameters: QueryParam[] = [
				{ param: 'userIds', value: userIds },
				{ param: 'rowIndex', value: 0 },
				{ param: 'rowCount', value: userIds.length },
			];
			userProfiles = await this.apiService.getCompanyUsersByCompanyId(
				companyId!,
				queryParameters
			);
			usersExist = userProfiles.length > 0;
		}

		if (usersExist) {
			this.dialog.open(CancelConfirmationComponent, {
				data: {
					dialogHeader: $localize`:@@addRoleUsers-confirmationHeader:Warning: User(s) already have role in company.`,
					dialogContent: $localize`:@@addRoleUsers-confirmationContent:${userProfiles.length} user(s) already have a role in your company.  Continuing will reassign the user(s) to the role created/selected.`,
					dialogPrimaryButtonText: 'Cancel',
					dialogSecondaryButtonText: 'Continue',
					secondaryFunction: () => {
						continueMethod();
					},
				},
			});
		} else {
			continueMethod();
		}
	}
}
